// Launch API vocabulary shared by the unified open path (openCapsuleClient).
//
// This module carries NO client of its own anymore (the legacy LaunchClient
// was retired in launch-unification Phase 4 — every open/run/stop/cancel goes
// through openCapsuleClient → /v1/launches). What remains here is the shared
// vocabulary both sides of that client use:
//   defaultBaseUrl / isLaunchOpenEnabled       … env plumbing + build flag
//   EmbedPolicy / classifyEmbedByUrl           … embed rendering hints
//   LaunchApiError / launchApiErrorFromBody /
//   LaunchConnectionError / LaunchPaywallOffer … the ONE error-body parsing


import { accountApiBase } from "../app/connection";

/** Public app-proxy base hosts whose strict single-label subdomains Ato
 * controls. This list describes the APP URL, not the shell page serving the
 * PWA: the production shell may be either ato.run or app.ato.run. */
export const APP_PROXY_BASE_HOSTS = ["app.ato.run", "stg-app.ato.run"] as const;

/** True only for an HTTPS Ato app-proxy session URL:
 * `https://<single-label>.<known-base-host>/...`. */
export function isAtoAppProxySessionUrl(appUrl: string): boolean {
  let url: URL;
  try {
    url = new URL(appUrl);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" || (url.port && url.port !== "443")) {
    return false;
  }
  const host = url.hostname.toLowerCase();
  return APP_PROXY_BASE_HOSTS.some((baseHost) => {
    const suffix = `.${baseHost}`;
    if (!host.endsWith(suffix)) return false;
    const label = host.slice(0, host.length - suffix.length);
    return label.length > 0 && !label.includes(".");
  });
}

export function defaultBaseUrl(): string {
  return accountApiBase();
}

/**
 * How the PWA may embed a ready session (ato-api#92). The server computes this
 * from capsule trust; the framing header on `<session>.app.ato.run` is
 * authoritative, this is only a rendering hint.
 *   trusted_unsandboxed → iframe WITHOUT sandbox (Ato-verified app)
 *   sandboxed           → iframe WITH a restrictive sandbox (default)
 *   external_only        → not embeddable; open in a new tab only
 */
export type EmbedPolicy = "trusted_unsandboxed" | "sandboxed" | "external_only";

/**
 * Client-side embed classification for an app URL whose backend `embed_policy`
 * isn't known (e.g. an existing run opened from the Dashboard/Run screen). A
 * STRICT single-label session under a known app-proxy base host is
 * Ato-controlled → embeddable (`sandboxed`); anything else (a Connected Runner
 * origin, a raw Fly URL) →
 * `external_only`. Mirrors the server's classifier; never returns
 * `trusted_unsandboxed` (that requires the verified gate, which only the launch
 * payload carries).
 */
export function classifyEmbedByUrl(appUrl: string): EmbedPolicy {
  return isAtoAppProxySessionUrl(appUrl) ? "sandboxed" : "external_only";
}

/**
 * Plan offer carried by a 402 `cloud_subscription_required` body (ato-api #136),
 * so a paywall can render a "Subscribe — $X/mo" CTA without a second round-trip.
 * `price_cents`/`currency` are null when the plan has no display price seeded.
 */
export interface LaunchPaywallOffer {
  plan_slug?: string;
  price_cents?: number | null;
  currency?: string | null;
  checkout_url_path?: string;
  /** Flat per-launch payment (402 `launch_payment_required`, billing v2): the
   *  one-time Stripe Checkout URL for exactly this launch. Absent on
   *  subscription 402s and when the server couldn't mint a session (the
   *  client then offers retry). */
  checkout_url?: string;
}

/** A typed launch API failure. `code`/`message` come from the server. */
export class LaunchApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Capability mismatch (422) → the runner capabilities the capsule needs. */
    readonly missingCapabilities?: string[],
    /** Paywall offer (402 cloud_subscription_required) → price + checkout path. */
    readonly offer?: LaunchPaywallOffer,
  ) {
    super(message);
    this.name = "LaunchApiError";
  }
}

/**
 * Build the typed LaunchApiError for a non-2xx launch-API response body —
 * the ONE parsing of code/message/missing_capabilities/402-offer
 * (openCapsuleClient wraps this into its own error map).
 */
export function launchApiErrorFromBody(
  status: number,
  json: unknown,
): LaunchApiError {
  const j = json as Record<string, unknown> | null;
  const str = (v: unknown): string | undefined =>
    typeof v === "string" ? v : undefined;
  const code = str(j?.["code"]) ?? str(j?.["error"]) ?? "launch_error";
  const message = str(j?.["message"]) ?? str(j?.["reason"]) ?? `HTTP ${status}`;
  const missing = Array.isArray(j?.["missing_capabilities"])
    ? (j!["missing_capabilities"] as unknown[]).filter(
        (x): x is string => typeof x === "string",
      )
    : undefined;
  // 402 paywall body (ato-api #136) carries the plan offer for a Subscribe CTA.
  const num = (v: unknown): number | null => (typeof v === "number" ? v : null);
  const offer =
    status === 402
      ? {
          plan_slug: str(j?.["plan_slug"]),
          price_cents: num(j?.["price_cents"]),
          currency: str(j?.["currency"]) ?? null,
          checkout_url_path: str(j?.["checkout_url_path"]),
          // Per-launch payment (billing v2): the launch-keyed Checkout URL.
          checkout_url: str(j?.["checkout_url"]),
        }
      : undefined;
  return new LaunchApiError(status, code, message, missing, offer);
}

export class LaunchConnectionError extends Error {
  constructor(cause?: unknown) {
    super(
      `Could not reach the Ato launch API. ${cause instanceof Error ? cause.message : String(cause)}`,
    );
    this.name = "LaunchConnectionError";
  }
}

/** Is the stable `/open/:capsule_ref` flow enabled in this build? Default off so
 *  it ships dormant until the backend `/v1/launches` flag is on in the same env. */
export function isLaunchOpenEnabled(): boolean {
  return import.meta.env.VITE_LAUNCH_OPEN_ENABLED === "true";
}

/**
 * Is "Run on Managed Cloud" offered in the NORMAL launch flow (GetRun)? Separate
 * from the internal demo-panel flag (managedCloudDemo.ts) so it can be rolled
 * out independently — it stays OFF until at least one public Store capsule is
 * actually managed-cloud runnable. Default off.
 */
export function isManagedCloudRunEnabled(): boolean {
  return import.meta.env.VITE_MANAGED_CLOUD_RUN_ENABLED === "true";
}
